/**
 * Was a chat-stream error a DELIBERATE refusal rather than a broken stream?
 *
 * The public AI routes sit behind two gates that both answer with a JSON body
 * and a 4xx: the header-only `guardPublicApiRoute` (`forbidden` /
 * `rate_limited`) and the BotID (Kasada) verdict (`forbidden`). A refusal is
 * the gate working, not an outage — but `useChat` surfaces it through the same
 * `onError` as a genuine failure, so it used to be reported as
 * `product-search-stream-error` and paged us by email.
 *
 * That is worse than noise. The headless fleet these gates exist to stop RUNS
 * THIS JAVASCRIPT (see the 2026-08-29 human-gate measurement), so the bots
 * were generating our alert mail themselves, at a volume that would bury a
 * real outage in the same inbox. Measured 2026-08-31 across four clean
 * 30-minute production windows: 156 of 156 refusals came from clients that
 * never loaded the page — no BotID challenge header at all — and ZERO real
 * browsers were refused.
 *
 * THE RISKY DIRECTION IS FALSE POSITIVES HERE: anything this call reports as
 * "deliberate" stops alerting. So it matches only our own two refusal codes,
 * parsed from a JSON body. A network drop, a 5xx, a truncated stream or an
 * HTML error page all fail to parse (or carry another code) and stay
 * reportable — which is the behaviour the tests pin.
 *
 * `useChat` throws with the response body as `error.message`, which is why the
 * body is what gets read here.
 */
export function isDeliberateRefusal(error: unknown): boolean {
  const code = refusalBody(error)?.error;
  return code === 'forbidden' || code === 'rate_limited';
}

/**
 * How long the rate limiter asked the shopper to wait (`retryAfterSeconds`,
 * sent with every `rate_limited` body by server/api-guard.ts), or null for
 * any other failure. The Retry pill waits it out instead of sending a request
 * the limiter will refuse again (2026-10-13).
 */
export function refusalRetryAfterSeconds(error: unknown): number | null {
  const body = refusalBody(error);
  if (body?.error !== 'rate_limited') return null;
  const seconds = body.retryAfterSeconds;
  return typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

function refusalBody(error: unknown): { error?: unknown; retryAfterSeconds?: unknown } | null {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  if (typeof message !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(message);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  return parsed as { error?: unknown; retryAfterSeconds?: unknown };
}
